import { create } from 'zustand';
import { API_BASE_URL } from '@/lib/config';
import { buildUserHeaders } from './userStore';

type Provider = 'openai' | 'anthropic';

export interface SystemStatus {
  aiReady: boolean;
  requiresSetup: boolean;
  hasAnyKey: boolean;
  activeProvider: Provider;
  defaultModel: string;
  providers: Record<Provider, boolean>;
  missingReasons: string[];
  message?: string;
  lastChecked: number;
}

interface SystemStatusStore {
  status: SystemStatus | null;
  isLoading: boolean;
  error?: string;
  loadStatus: () => Promise<void>;
}

export const useSystemStatusStore = create<SystemStatusStore>((set) => ({
  status: null,
  isLoading: false,
  error: undefined,

  loadStatus: async () => {
    set({ isLoading: true, error: undefined });
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(`${API_BASE_URL}/system/status`, {
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        signal: controller.signal
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Status check failed (${response.status})`);
      }

      const data = await response.json();
      const status: SystemStatus = {
        aiReady: Boolean(data.aiReady),
        requiresSetup: Boolean(data.requiresSetup),
        hasAnyKey: Boolean(data.hasAnyKey),
        activeProvider: data.activeProvider || 'openai',
        defaultModel: data.defaultModel || 'gpt-4o-mini',
        providers: data.providers || { openai: false, anthropic: false },
        missingReasons: Array.isArray(data.missingReasons) ? data.missingReasons : [],
        message: data.message,
        lastChecked: Date.now()
      };

      set({ status, isLoading: false });
    } catch (error: any) {
      const aborted = error?.name === 'AbortError';
      const message = aborted
        ? 'Status check timed out. Please retry.'
        : error instanceof Error
          ? error.message
          : 'Failed to check system status';
      set({ error: message, isLoading: false });
    } finally {
      clearTimeout(timeout);
    }
  }
}));
